'use client';

import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PrismicLink } from '@prismicio/react';
import { ImageField, LinkField } from '@prismicio/types';
import classNames from 'classnames';
import { useState } from 'react';
import { Button } from '@get-mosh/msh-shared';
import styles from './table-cell.module.scss';
import { PrismicNextImage } from '@prismicio/next';

export const TableCell = ({
    cellBoldedText,
    cellText,
    cellImage,
    header,
    column,
    category,
    cellLink,
    cellLinkLabel,
    cellViewMore,
    tableColCount,
    colVisibleOnMobile,
}: {
    cellBoldedText?: string;
    cellText?: string;
    header?: boolean;
    column: number;
    category?: boolean;
    cellImage?: ImageField;
    cellLink?: LinkField;
    cellViewMore?: string;
    cellLinkLabel?: string;
    tableColCount: number;
    colVisibleOnMobile?: 2 | 3;
}) => {
    // Determine col fill based on col index
    const fillMap: Record<number, string> = {
        1: 'bg-transparent',
        2: 'bg-green-light',
        3: 'bg-transparent md:bg-sand',
        4: 'bg-transparent md:bg-transparent',
    };
    // Hide the relevant column on mobile
    const columnIsHiddenOnMobile =
        column > 2 && column - 1 !== colVisibleOnMobile;
    const mobileHiddenColumnStyles = columnIsHiddenOnMobile
        ? 'hidden md:flex'
        : 'flex';

    const [isCollapsed, setIsCollapsed] = useState(true);

    if (header)
        return (
            <div
                className={classNames(
                    styles['cell'],
                    styles['header-cell'],
                    fillMap[column] ?? 'bg-transparent',
                    column !== 1 && 'rounded-tl-xl rounded-tr-xl',
                    tableColCount === 3 ? 'w-[33.3%] md:w-[25%]' : 'w-[33.3%]',
                    mobileHiddenColumnStyles,
                )}
                role="columnheader"
            >
                {cellImage?.url ? (
                    <picture className="max-w-[3.125rem] md:max-w-[4.375rem]">
                        <PrismicNextImage field={cellImage} />
                    </picture>
                ) : (
                    <span className="text-center font-bolder text-sm uppercase tracking-[0.15rem]">
                        {cellText}
                    </span>
                )}
            </div>
        );
    return (
        <div
            className={classNames(
                styles['cell'],
                styles['column-cell'],
                fillMap[column] ?? 'bg-transparent',
                tableColCount === 3 ? 'w-[33.3%] md:w-[25%]' : 'w-[33.3%]',
                mobileHiddenColumnStyles,
            )}
            role="cell"
        >
            <span className="flex flex-col">
                {cellImage?.url && (
                    <picture
                        className={classNames(
                            'mx-auto max-w-[2rem] md:max-w-[2.5rem]',
                            cellText || (cellLink && cellLinkLabel)
                                ? 'mb-3'
                                : '',
                        )}
                    >
                        <PrismicNextImage field={cellImage} />
                    </picture>
                )}
                {cellBoldedText && <strong>{cellBoldedText}</strong>}
                <p
                    className={classNames(
                        cellLink && cellLinkLabel ? 'mb-2' : 'mb-0',
                        category
                            ? 'font-demi text-regular'
                            : 'text-base leading-6',
                    )}
                >
                    {cellText}
                </p>
                {cellLink && cellLinkLabel && (
                    <PrismicLink field={cellLink}>
                        <span className="font-demi text-xs leading-5 tracking-tight underline">
                            {cellLinkLabel}
                        </span>
                    </PrismicLink>
                )}
                {cellViewMore && (
                    <>
                        <Button
                            variant="tertiary"
                            onClick={() => setIsCollapsed(!isCollapsed)}
                        >
                            {isCollapsed ? (
                                <>
                                    View more
                                    <FontAwesomeIcon
                                        icon={faChevronDown}
                                        className="ml-1 !h-3"
                                    />
                                </>
                            ) : (
                                <>
                                    View less
                                    <FontAwesomeIcon
                                        icon={faChevronUp}
                                        className="ml-1 !h-3"
                                    />
                                </>
                            )}
                        </Button>
                        <div
                            className={classNames(
                                styles['collapsed-cell'],
                                `${
                                    isCollapsed
                                        ? styles['collapsed']
                                        : styles['expanded']
                                }`,
                            )}
                            aria-expanded={isCollapsed}
                        >
                            {cellViewMore}
                        </div>
                    </>
                )}
            </span>
        </div>
    );
};
